import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getDeviceUuid, getRegistrationContext } from '../api';
import { readRegistrationDraft, registrationOwner, setRegistrationOwner, invalidateRegistrationOwner,
    REGISTRATION_DRAFT_EVENT, REGISTRATION_DRAFT_KEY } from '../utils/registrationDraft';

const Context = createContext(null);
export const useRegistrationDraft = () => useContext(Context);

export default function RegistrationDraftProvider({ children, enabled = true }) {
    const [draft, setDraft] = useState(null);
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);
    const seq = useRef(0);
    const refresh = useCallback(async () => {
        if (!enabled) return;
        const request = ++seq.current;
        const uuid = getDeviceUuid();
        setFailed(false);
        try {
            const result = await getRegistrationContext(uuid);
            if (seq.current !== request || getDeviceUuid() !== uuid) return;
            if (!setRegistrationOwner(uuid, result.context)) throw new Error('context unavailable');
            setDraft(readRegistrationDraft()); setReady(true);
        } catch {
            if (seq.current !== request) return;
            setFailed(true);
        }
    }, [enabled]);
    useEffect(() => {
        invalidateRegistrationOwner({ clear: false });
        if (!enabled) { setDraft(null); setReady(false); return; }
        refresh();
        const changed = () => setDraft(readRegistrationDraft());
        const invalidated = () => { seq.current++; setReady(false); setDraft(null); refresh(); };
        const storage = event => { if (event.key === REGISTRATION_DRAFT_KEY || event.key === null) changed(); };
        window.addEventListener(REGISTRATION_DRAFT_EVENT, changed);
        window.addEventListener('kkomok-account-context-changed', invalidated);
        window.addEventListener('storage', storage);
        window.addEventListener('online', refresh);
        window.addEventListener('native-app-foreground', refresh);
        return () => {
            seq.current++;
            window.removeEventListener(REGISTRATION_DRAFT_EVENT, changed);
            window.removeEventListener('kkomok-account-context-changed', invalidated);
            window.removeEventListener('storage', storage);
            window.removeEventListener('online', refresh);
            window.removeEventListener('native-app-foreground', refresh);
        };
    }, [refresh, enabled]);
    return <Context.Provider value={{ draft, ready, failed, refresh, owner: registrationOwner() }}>{children}</Context.Provider>;
}
